// Cliente HTTP do backend Bela Vista II.
//
// Em produção, /api/* é proxiado pelo nginx para o container backend.
// Em dev (npm run dev), Vite faz proxy para http://localhost:3001 (vite.config.ts).

import type {
  ContaAPagar,
  Comprovante,
  ExtratoLancamento,
  Fornecedor,
  PlanoConta,
  PlanoContaNode,
  ContaBancaria,
  Socio,
  Aporte,
  Aplicacao,
  RetencaoTipo,
  FornecedorTotal,
  MesTotal,
  PagtoDia,
  Workflow,
  TipoImposto,
  ExtratoSugestao,
  ExtratoCandidato,
} from '../types';

const BASE = '/api';

function handleUnauthorized(path: string) {
  if (!path.startsWith('/auth/login')) {
    window.dispatchEvent(new Event('bv:unauthorized'));
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'include',
    ...init,
  });
  if (!res.ok) {
    if (res.status === 401) handleUnauthorized(path);
    const body = await res.text().catch(() => '');
    let parsed: unknown = body;
    try { parsed = JSON.parse(body); } catch { /* texto puro */ }
    const err = new Error(`HTTP ${res.status} ${path}`) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = parsed;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) {
    if (res.status === 401) handleUnauthorized(path);
    const body = await res.text().catch(() => '');
    const err = new Error(`HTTP ${res.status} ${path} :: ${body.slice(0, 200)}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

function qs(params?: Record<string, unknown>): string {
  const s = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  });
  const str = s.toString();
  return str ? `?${str}` : '';
}

// ===== Tipos auxiliares =====

export interface AuthUser {
  sub: number;
  email: string;
  nome: string;
  role: 'admin' | 'user';
}

export interface UserRow {
  id: number;
  email: string;
  nome: string;
  role: 'admin' | 'user';
  ativo: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface Lista<T> { items: T[]; count: number; total: number }

export interface ListContasParams {
  status?: 'todos' | 'pendente' | 'aguardando' | 'pago' | 'atrasadas' | 'a_vencer';
  tipo?: 'despesa' | 'receita';
  fornecedor?: string;
  fornecedor_id?: number;
  origem?: string;
  classe_id?: number;
  departamento_id?: number;
  categoria_id?: number;
  classificacao_status?: 'pendente' | 'sugerida' | 'auto' | 'confirmada';
  guias?: 'true' | 'false' | 'somente';
  sem_nf?: boolean;
  vencimento_de?: string;
  vencimento_ate?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CriarContaBody {
  tipo: 'despesa' | 'receita';
  fornecedor?: string;
  fornecedor_id?: number;
  cnpj_cpf?: string | null;
  numero_doc?: string | null;
  descricao?: string | null;
  valor?: number | null;
  vencimento?: string | null;
  previsao_pagamento?: string | null;
  linha_digitavel?: string | null;
  pix?: string | null;
  forma_pagamento?: 'boleto' | 'pix' | 'transferencia' | 'cartao' | 'outro' | null;
  parcelas?: number;
  plano_conta_id?: number | null;
  conta_bancaria_id?: number | null;
  quadra_lote?: string | null;
  nf_dispensada?: boolean;
  retencoes?: Array<{ tipo_imposto: TipoImposto; valor: number; aliquota?: number }>;
}

export interface PagarBody {
  pago_em: string;
  conta_bancaria_id?: number;
  multa?: number;
  juros?: number;
  desconto?: number;
  valor_pago?: number;
}

export interface DashboardKpis {
  periodo: { ini: string; fim: string; ano: number; mes: number };
  a_pagar_mes: { cnt: number; total: number };
  atrasadas:   { cnt: number; total: number };
  pagas_mes:   { cnt: number; total: number };
  nfs_faltantes: { cnt: number };
  pagas_sem_nf: { cnt: number; total: number };
  comp_orfaos: { cnt: number; total: number };
  proximas_7d: Array<{
    id: number; fornecedor: string; numero_doc: string | null; descricao: string | null;
    valor: number; vencimento: string; pix: string | null;
  }>;
}

export const api = {
  // Health
  health: () => http<{ status: string; uptime: number }>('/health'),

  // ===== Auth =====
  login: (email: string, senha: string) =>
    http<{ user: AuthUser }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, senha }) }),
  logout: () => http<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => http<{ user: AuthUser }>('/auth/me'),
  listUsers: () => http<{ items: UserRow[] }>('/auth/users'),
  createUser: (u: { email: string; nome: string; senha: string; role?: 'admin' | 'user' }) =>
    http<UserRow>('/auth/users', { method: 'POST', body: JSON.stringify(u) }),
  patchUser: (id: number, patch: { nome?: string; senha?: string; role?: 'admin' | 'user'; ativo?: boolean }) =>
    http<UserRow>(`/auth/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  // ===== Dashboard =====
  kpis: (ano?: number, mes?: number) => http<DashboardKpis>(`/dashboard/kpis${qs({ ano, mes })}`),
  pagtos30d: () => http<{ items: PagtoDia[] }>('/dashboard/pagtos-30d'),
  workflows: () => http<{ items: Workflow[]; _note?: string }>('/dashboard/workflows'),
  relMes: () => http<{ items: MesTotal[] }>('/dashboard/rel/mes'),
  relFornecedor: () => http<{ items: FornecedorTotal[] }>('/dashboard/rel/fornecedor'),

  // ===== Contas / Lançamentos =====
  listContas: (params?: ListContasParams) => http<Lista<ContaAPagar>>(`/contas${qs(params as Record<string, unknown>)}`),
  getConta: (id: number) => http<ContaAPagar>(`/contas/${id}`),
  criarConta: (body: CriarContaBody) =>
    http<{ ids: (number | string)[]; parcelas: number }>('/contas', { method: 'POST', body: JSON.stringify(body) })
      // o Postgres pode devolver o id como string (bigint) — normaliza para número
      .then((r) => ({ ...r, ids: r.ids.map((x) => Number(x)) })),
  editarConta: (id: number, patch: Partial<ContaAPagar>) =>
    http<ContaAPagar>(`/contas/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  putRetencoes: (id: number, retencoes: Array<{ tipo_imposto: TipoImposto; valor: number; aliquota?: number }>) =>
    http<ContaAPagar>(`/contas/${id}/retencoes`, { method: 'PUT', body: JSON.stringify({ retencoes }) }),
  pagarConta: (id: number, body: PagarBody) =>
    http<{ id: number; status: string; pago_em: string }>(`/contas/${id}/pagar`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  classificarConta: (id: number, plano_conta_id: number, definir_padrao?: boolean) =>
    http<ContaAPagar>(`/contas/${id}/classificar`, {
      method: 'POST', body: JSON.stringify({ plano_conta_id, definir_padrao }),
    }),
  excluirConta: (id: number, motivo: string) =>
    http<{ ok: true }>(`/contas/${id}`, { method: 'DELETE', body: JSON.stringify({ motivo }) }),
  origens: () => http<{ items: string[] }>('/contas/_meta/origens'),
  getContaAnexo: (id: number) =>
    http<{ pdf_base64: string; mime: string } | { error: string; email_link?: string }>(`/contas/${id}/anexo`),
  uploadContaAnexo: (id: number, file: File) => {
    const fd = new FormData(); fd.append('file', file);
    return upload<{ ok: true; anexo_nome: string }>(`/contas/${id}/anexo`, fd);
  },

  // ===== Fornecedores =====
  listFornecedores: (params?: { search?: string; com_padrao?: boolean; incluir_inativos?: boolean }) =>
    http<Lista<Fornecedor>>(`/fornecedores${qs(params as Record<string, unknown>)}`),
  getFornecedor: (id: number) => http<Fornecedor>(`/fornecedores/${id}`),
  criarFornecedor: (body: { nome: string; cnpj_cpf?: string; categoria_padrao_id?: number }) =>
    http<Fornecedor>('/fornecedores', { method: 'POST', body: JSON.stringify(body) }),
  patchFornecedor: (id: number, patch: { nome?: string; cnpj_cpf?: string; categoria_padrao_id?: number | null; ativo?: boolean }) =>
    http<Fornecedor>(`/fornecedores/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  enriquecerFornecedor: (id: number) =>
    http<{ ok: boolean; motivo?: string }>(`/fornecedores/${id}/enriquecer`, { method: 'POST' }),
  mesclarFornecedor: (id: number, destino_id: number) =>
    http<{ ok: true }>(`/fornecedores/${id}/mesclar`, { method: 'POST', body: JSON.stringify({ destino_id }) }),

  // ===== Plano de contas =====
  listPlano: () => http<{ items: PlanoConta[] }>('/plano-contas'),
  arvorePlano: () => http<{ items: PlanoContaNode[] }>('/plano-contas/arvore'),
  criarPlano: (body: { nome: string; parent_id?: number | null; ordem?: number }) =>
    http<PlanoConta>('/plano-contas', { method: 'POST', body: JSON.stringify(body) }),
  patchPlano: (id: number, patch: { nome?: string; ordem?: number; ativo?: boolean }) =>
    http<PlanoConta>(`/plano-contas/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  excluirPlano: (id: number) => http<{ ok: true }>(`/plano-contas/${id}`, { method: 'DELETE' }),
  mesclarPlano: (id: number, destino_id: number) =>
    http<{ ok: true }>(`/plano-contas/${id}/mesclar`, { method: 'POST', body: JSON.stringify({ destino_id }) }),

  // ===== Cadastros =====
  listContasBancarias: () => http<{ items: ContaBancaria[] }>('/contas-bancarias'),
  criarContaBancaria: (body: Partial<ContaBancaria>) =>
    http<ContaBancaria>('/contas-bancarias', { method: 'POST', body: JSON.stringify(body) }),
  patchContaBancaria: (id: number, patch: Partial<ContaBancaria>) =>
    http<ContaBancaria>(`/contas-bancarias/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  listSocios: () => http<{ items: Socio[] }>('/socios'),
  listAportes: (params?: { socio_id?: number; tipo?: string; de?: string; ate?: string }) =>
    http<{ items: Aporte[] }>(`/aportes${qs(params as Record<string, unknown>)}`),
  criarAporte: (body: Partial<Aporte>) => http<Aporte>('/aportes', { method: 'POST', body: JSON.stringify(body) }),
  patchAporte: (id: number, patch: Partial<Aporte>) => http<Aporte>(`/aportes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  excluirAporte: (id: number) => http<{ ok: true }>(`/aportes/${id}`, { method: 'DELETE' }),
  listAplicacoes: () => http<{ items: Aplicacao[] }>('/aplicacoes'),
  criarAplicacao: (body: Partial<Aplicacao>) => http<Aplicacao>('/aplicacoes', { method: 'POST', body: JSON.stringify(body) }),
  patchAplicacao: (id: number, patch: Partial<Aplicacao>) => http<Aplicacao>(`/aplicacoes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  excluirAplicacao: (id: number) => http<{ ok: true }>(`/aplicacoes/${id}`, { method: 'DELETE' }),
  listRetencaoTipos: () => http<{ items: RetencaoTipo[] }>('/retencao-tipos'),
  patchRetencaoTipo: (codigo: string, patch: Partial<RetencaoTipo>) =>
    http<RetencaoTipo>(`/retencao-tipos/${codigo}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  // ===== Comprovantes =====
  listComprovantes: (params?: { match_status?: MatchStatus_; fornecedor?: string; data_de?: string; data_ate?: string; limit?: number; offset?: number }) =>
    http<Lista<Comprovante>>(`/comprovantes${qs(params as Record<string, unknown>)}`),
  getComprovantePdf: (id: number) => http<{ pdf_base64: string; mime: string }>(`/comprovantes/${id}/pdf`),
  vincularComprovante: (id: number, contas_a_pagar_id: number) =>
    http<{ id: number; contas_a_pagar_id: number }>(`/comprovantes/${id}/vincular`, {
      method: 'POST', body: JSON.stringify({ contas_a_pagar_id }),
    }),
  uploadComprovante: (file: File, fields?: { contas_a_pagar_id?: number; fornecedor?: string; valor?: number; data_pagamento?: string }) => {
    const fd = new FormData(); fd.append('file', file);
    Object.entries(fields ?? {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, String(v)); });
    return upload<{ id: number; match_status: string }>('/comprovantes', fd);
  },

  // ===== Extrato =====
  listExtrato: (params?: { tipo?: 'D' | 'C'; conciliado?: 'true' | 'false'; mes_ano?: string; banco?: string; limit?: number; offset?: number }) =>
    http<Lista<ExtratoLancamento>>(`/extrato${qs(params as Record<string, unknown>)}`),
  reclassificarExtrato: (id: number, tipo: 'D' | 'C') =>
    http<{ id: number; tipo: string }>(`/extrato/${id}/tipo`, { method: 'PATCH', body: JSON.stringify({ tipo }) }),
  vincularExtrato: (id: number, conta_a_pagar_id: number | null) =>
    http<{ id: number; conta_a_pagar_id: number | null; conciliado: boolean }>(`/extrato/${id}/vincular`, {
      method: 'POST', body: JSON.stringify({ conta_a_pagar_id }),
    }),
  // Sugestões de conciliação (valor ±2% + data ±5 dias), calculadas no backend sobre TODAS as contas
  extratoSugestoes: () => http<{ items: ExtratoSugestao[] }>('/extrato/sugestoes'),
  extratoCandidatos: (id: number) => http<{ items: ExtratoCandidato[] }>(`/extrato/${id}/candidatos`),
  importExtrato: (file: File, banco: string, conta?: string) => {
    const fd = new FormData(); fd.append('file', file); fd.append('banco', banco);
    if (conta) fd.append('conta', conta);
    return upload<{ total: number; inserted: number; skipped: number }>('/extrato/import', fd);
  },
  // Enriquecimento de identidade (nome + CPF/CNPJ do favorecido) via XLSX da Caixa.
  // Merge sobre lançamentos já importados por OFX — não insere linhas novas.
  enriquecerExtrato: (file: File, dry?: boolean) => {
    const fd = new FormData(); fd.append('file', file);
    if (dry) fd.append('dry', 'true');
    return upload<{
      dry: boolean; total_arquivo: number; com_identidade: number;
      casadas: number; atualizadas: number;
      sem_correspondencia: string[]; sem_correspondencia_total: number;
    }>('/extrato/enriquecer', fd);
  },

  // ===== Relatórios =====
  relatorio: <T = unknown>(nome: RelatorioNome, params?: { de?: string; ate?: string; conta_bancaria_id?: number; ano?: number }) =>
    http<T>(`/relatorios/${nome}${qs(params as Record<string, unknown>)}`),
  // URL para baixar CSV (usar em <a href> ou window.location)
  relatorioCsvUrl: (nome: RelatorioNome, params?: { de?: string; ate?: string; conta_bancaria_id?: number; ano?: number }) =>
    `${BASE}/relatorios/${nome}${qs({ ...(params ?? {}), format: 'csv' })}`,
};

export type MatchStatus_ = 'matched' | 'orphan' | 'pending';
export type RelatorioNome =
  | 'resumo-despesas'
  | 'resumo-despesas-mes'
  | 'resumo-receitas'
  | 'resumo-geral'
  | 'conciliacao-bancaria'
  | 'dinamica'
  | 'retencoes';
